/**
 * Εμφάνιση ανά οργανισμό με παράκαμψη ανά κατάστημα (απόφαση ιδιοκτήτη 12/10/2026, migration 263).
 *
 * Ο server δίνει στο store.theme (/api/auth/me, pin-login, store-login) τον συνδυασμό
 * «κατάστημα → οργανισμός → null» (backend/utils/storeTheme.js). Εδώ γίνεται CSS:
 *
 *   brand      → το ΕΝΑ χρώμα ενέργειας της νέας εμφάνισης (σήμερα το μπλε --brand) και όλα όσα
 *                παράγονται από αυτό: --brand-soft, --brand-line, --on-brand (κείμενο ΠΑΝΩ στο χρώμα),
 *                --brand-ink (το χρώμα ως ΚΕΙΜΕΝΟ πάνω σε ανοιχτό φόντο — το κίτρινο σκουραίνει),
 *                --primary, --ring και τα παλιά --workspace-accent* / --cashier-accent*.
 *   surface    → οι επιφάνειες του ανοιχτού θέματος (paper = το στατικό «Χαρτί» του CSS· προεπιλογή
 *                εφαρμογής από 15/10/2026 το 'brand' «Στο χρώμα», DEFAULT_SURFACE). Το σκούρο μένει
 *                «Γραφίτης», με την απόχρωση του brand μόνο στο 'brand'.
 *   appearance → ανοιχτό/σκούρο όταν η ΣΥΣΚΕΥΗ δεν έχει δική της επιλογή: getStoreDefaultAppearance()
 *                (το διαβάζει το ThemeContext).
 *
 * Το applyStoreTheme γράφει ένα <style id="store-theme"> ως ΤΕΛΕΥΤΑΙΟ παιδί του <head>: οι κανόνες
 * του έχουν την ίδια ειδικότητα με το index.css / CashierV2.css / RoleV2.css (που ξαναορίζουν τα
 * tokens πάνω στο .cashier-workspace / .role-workspace) και κερδίζουν επειδή έρχονται μετά.
 * Τίποτα δεν γράφεται μόνο για το «Χαρτί» με το μπλε (= το στατικό CSS)· χωρίς θέμα γράφεται η προεπιλογή
 * εφαρμογής («Στο χρώμα», ανοιχτό).
 */
import { useSyncExternalStore } from 'react';

export type ThemeSurface = 'paper' | 'neutral' | 'cool' | 'brand';
export type ThemeAppearance = 'light' | 'dark' | 'system';

/** Ό,τι αποθηκεύεται (οργανισμός ή κατάστημα): κάθε πεδίο προαιρετικό, null = κληρονομείται. */
export interface StoreTheme {
  brand?: string | null;
  surface?: ThemeSurface | null;
  appearance?: ThemeAppearance | null;
}

/** Τι ισχύει: πάντα και τα τρία πεδία· null = προεπιλογή εφαρμογής. */
export interface ResolvedStoreTheme {
  brand: string | null;
  surface: ThemeSurface | null;
  appearance: ThemeAppearance | null;
}

export const THEME_SURFACES: readonly ThemeSurface[] = ['paper', 'neutral', 'cool', 'brand'];
export const THEME_APPEARANCES: readonly ThemeAppearance[] = ['light', 'dark', 'system'];

/** Το σημερινό μπλε: hsl(224 64% 51%) του index.css. Ίδιο με «κανένα χρώμα» — δεν γράφεται CSS. */
export const DEFAULT_BRAND = '#325DD2';
/**
 * Προεπιλογή εφαρμογής (απόφαση ιδιοκτήτη 15/10/2026, πριν την παραγωγή): φόντο «Στο χρώμα» και ανοιχτό.
 * Το στατικό CSS (index.css / CashierV2.css / RoleV2.css) μένει «Χαρτί»/«Γραφίτης», γι' αυτό η προεπιλογή
 * ΓΡΑΦΕΤΑΙ ως θέμα (brand-tinted επιφάνειες) και το «Χαρτί» είναι πλέον η επιλογή που δεν γράφει τίποτα.
 */
export const DEFAULT_SURFACE: ThemeSurface = 'brand';
export const DEFAULT_APPEARANCE: ThemeAppearance = 'light';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
export const isBrandHex = (v: unknown): v is string => typeof v === 'string' && HEX_RE.test(v);
export const isDefaultBrand = (hex: string | null | undefined): boolean =>
  !hex || hex.toUpperCase() === DEFAULT_BRAND;

/** Οι 12 έτοιμες αποχρώσεις της οθόνης ρυθμίσεων (το όνομα από το settings:theme.swatch.<id>). */
export const BRAND_SWATCHES: ReadonlyArray<{ id: string; hex: string }> = [
  { id: 'default', hex: DEFAULT_BRAND },
  { id: 'yellow', hex: '#F5B700' },  // Κουλουράδες
  { id: 'orange', hex: '#EA580C' },
  { id: 'red', hex: '#DC2626' },
  { id: 'rose', hex: '#E11D48' },
  { id: 'violet', hex: '#7C3AED' },
  { id: 'green', hex: '#15803D' },
  { id: 'teal', hex: '#0F766E' },
  { id: 'cyan', hex: '#0891B2' },
  { id: 'slate', hex: '#475569' },
  { id: 'black', hex: '#18181B' },
  { id: 'brown', hex: '#8B5E3C' },
];

/* ───────────────────────── δεδομένα ───────────────────────── */

/** Ανεκτικό: ό,τι δεν είναι έγκυρο αγνοείται (= κληρονομείται). Χρώμα σε κεφαλαία. */
export function normalizeTheme(raw: unknown): StoreTheme | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: StoreTheme = {};
  if (isBrandHex(r.brand)) out.brand = r.brand.toUpperCase();
  if (THEME_SURFACES.includes(r.surface as ThemeSurface)) out.surface = r.surface as ThemeSurface;
  if (THEME_APPEARANCES.includes(r.appearance as ThemeAppearance)) out.appearance = r.appearance as ThemeAppearance;
  return Object.keys(out).length > 0 ? out : null;
}

/** Ανά πεδίο: κατάστημα → οργανισμός → null. Ίδιος κανόνας με το backend/utils/storeTheme.js. */
export function resolveTheme(org: unknown, store: unknown): ResolvedStoreTheme {
  const o = normalizeTheme(org) || {};
  const s = normalizeTheme(store) || {};
  return {
    brand: s.brand ?? o.brand ?? null,
    surface: s.surface ?? o.surface ?? null,
    appearance: s.appearance ?? o.appearance ?? null,
  };
}

const toResolved = (theme: unknown): ResolvedStoreTheme | null => {
  const t = normalizeTheme(theme);
  if (!t) return null;
  return { brand: t.brand ?? null, surface: t.surface ?? null, appearance: t.appearance ?? null };
};

/* ───────────────────────── χρώμα ───────────────────────── */

export interface Hsl { h: number; s: number; l: number }
type Rgb = [number, number, number];

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round1 = (v: number) => Math.round(v * 10) / 10;
// Ένα δεκαδικό και στην απόχρωση: με ακέραιη το #F5B700 γινόταν #F5B800.
const hsl = (h: number, s: number, l: number): Hsl => ({ h: round1(((h % 360) + 360) % 360) % 360, s: round1(clamp(s, 0, 100)), l: round1(clamp(l, 0, 100)) });

export function hexToHsl(hex: string): Hsl {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return hsl(0, 0, l * 100);
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return hsl(h * 60, s * 100, l * 100);
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const S = s / 100;
  const L = l / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = L - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

export const hslToHex = (c: Hsl): string =>
  `#${hslToRgb(c).map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** Σχετική φωτεινότητα WCAG 2.x. */
export function relativeLuminance(c: Hsl): number {
  const [r, g, b] = hslToRgb(c).map((v) => {
    const x = clamp(v, 0, 255) / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: Hsl, b: Hsl): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Τριάδα για τα tokens (`224 64% 51%`): το Tailwind τα γράφει hsl(var(--x)). */
export const triplet = (c: Hsl): string => `${c.h} ${c.s}% ${c.l}%`;
const full = (c: Hsl): string => `hsl(${triplet(c)})`;

const WHITE = hsl(0, 0, 100);
/** «Σχεδόν μαύρο» στην απόχρωση του χρώματος — ζεστό στο κίτρινο, όχι γκρίζο. */
const nearBlack = (c: Hsl): Hsl => hsl(c.h, Math.min(20, c.s), 9);
const AA = 4.5;

/** Κείμενο ΠΑΝΩ στο χρώμα: λευκό όταν φτάνει 4.5:1, αλλιώς σχεδόν μαύρο· αν κανένα, ό,τι διαβάζεται καλύτερα. */
export function textOn(fill: Hsl): Hsl {
  const white = contrastRatio(WHITE, fill);
  if (white >= AA) return WHITE;
  const dark = nearBlack(fill);
  const black = contrastRatio(dark, fill);
  if (black >= AA) return dark;
  return black > white ? dark : WHITE;
}

/** Ίδια απόχρωση/κορεσμός, φωτεινότητα που φτάνει `target` αντίθεση με ΟΛΑ τα φόντα (σκουραίνει ή ανοίγει). */
function inkFor(c: Hsl, backgrounds: Hsl[], direction: -1 | 1, target = AA): Hsl {
  let ink = c;
  const ok = (x: Hsl) => backgrounds.every((bg) => contrastRatio(x, bg) >= target);
  while (!ok(ink) && ink.l > 4 && ink.l < 96) ink = hsl(ink.h, ink.s, ink.l + 0.5 * direction);
  return ink;
}

/* ───────────────────────── επιφάνειες ───────────────────────── */

interface Surfaces {
  s0: Hsl; s1: Hsl; s2: Hsl; s3: Hsl; s4: Hsl;
  line: Hsl; lineStrong: Hsl;
  t1: Hsl; t2: Hsl; t3: Hsl; t4: Hsl;
}

const pal = (rows: Array<[number, number, number]>): Surfaces => {
  const [s0, s1, s2, s3, s4, line, lineStrong, t1, t2, t3, t4] = rows.map(([h, s, l]) => hsl(h, s, l));
  return { s0, s1, s2, s3, s4, line, lineStrong, t1, t2, t3, t4 };
};

/** «Χαρτί» — ίδιο με το :root.look-v2:not(.dark) του index.css και το CashierV2.css. */
const PAPER = pal([[38, 22, 90], [38, 26, 93.5], [40, 30, 97], [38, 20, 89], [38, 16, 83], [38, 14, 84], [36, 12, 74],
  [30, 14, 17], [30, 10, 32], [30, 8, 44], [30, 7, 50]]);
/** Ουδέτερο γκρι (το δείγμα «γκρι» της επιλογής παλέτας, 12/10): σελίδα 91.5%, κάρτες 96%. */
const NEUTRAL = pal([[220, 12, 88], [220, 13, 91.5], [220, 16, 96], [220, 12, 88], [220, 10, 82], [220, 11, 83], [220, 9, 73],
  [222, 20, 16], [220, 12, 31], [220, 9, 44], [220, 8, 50]]);
/** Ψυχρό: μπλε-γκρι, ίδια σκάλα φωτεινότητας. */
const COOL = pal([[214, 24, 88.5], [214, 28, 92], [214, 32, 96.5], [214, 22, 88], [214, 18, 82], [214, 18, 83], [214, 14, 73],
  [220, 30, 15], [218, 20, 30], [216, 13, 43], [216, 11, 49]]);
/** «Γραφίτης» — ίδιο με το :root.look-v2.dark του index.css. */
const GRAPHITE = pal([[220, 7, 12], [220, 7, 15], [220, 7, 19], [220, 6, 24], [220, 5, 31], [220, 6, 25], [220, 5, 33],
  [220, 14, 90], [220, 9, 78], [220, 6, 63], [220, 5, 54]]);

/** Πόσο «χρώμα» έχει το brand (0 = γκρι/μαύρο → οι αποχρώσεις του μένουν ουδέτερες). */
const chroma = (b: Hsl) => clamp(b.s / 40, 0, 1);

/** Ανοιχτό «brand»: τα βήματα φωτεινότητας του «Χαρτιού» στην απόχρωση του brand, κορεσμός 12–18%. */
function brandLight(b: Hsl): Surfaces {
  const k = chroma(b);
  const S = (s: number) => s * k;
  return pal([[b.h, S(14), 90], [b.h, S(16), 93.5], [b.h, S(18), 97], [b.h, S(13), 89], [b.h, S(11), 83], [b.h, S(10), 84], [b.h, S(8), 74],
    [b.h, S(14), 17], [b.h, S(10), 32], [b.h, S(8), 44], [b.h, S(7), 50]]);
}

/** Σκούρο «brand»: ο «Γραφίτης» στην απόχρωση του brand, κορεσμός 5–7%. */
function brandDark(b: Hsl): Surfaces {
  const k = chroma(b);
  const S = (s: number) => s * k;
  return pal([[b.h, S(7), 12], [b.h, S(7), 15], [b.h, S(7), 19], [b.h, S(6), 24], [b.h, S(5), 31], [b.h, S(6), 25], [b.h, S(5), 33],
    [b.h, S(12), 90], [b.h, S(8), 78], [b.h, S(6), 63], [b.h, S(5), 54]]);
}

function lightSurfaces(surface: ThemeSurface, brand: Hsl): Surfaces {
  if (surface === 'neutral') return NEUTRAL;
  if (surface === 'cool') return COOL;
  if (surface === 'brand') return brandLight(brand);
  return PAPER;
}

/** Οθόνες ρόλων (PDA/διαχείριση): μία βαθμίδα πιο ανοιχτά, όπως το RoleV2.css (εκεί η σελίδα είναι το surface-0). */
function roleVariant(p: Surfaces): Surfaces {
  const up = (c: Hsl, dl: number, ds: number) => hsl(c.h, c.s > 0 ? c.s + ds : 0, c.l + dl);
  return { ...p, s0: up(p.s0, 2, 2), s1: up(p.s1, 2, 2), s2: up(p.s2, 0.5, 0) };
}

/* ───────────────────────── brand ───────────────────────── */

interface BrandTokens { fill: Hsl; on: Hsl; soft: Hsl; line: Hsl; ink: Hsl; hover: Hsl }

/** Οι σημερινές τιμές του index.css / CashierV2.css (μπλε). */
const DEFAULT_LIGHT_BRAND: BrandTokens = {
  fill: hsl(224, 64, 51), on: WHITE, soft: hsl(224, 85, 96), line: hsl(224, 70, 86), ink: hsl(224, 64, 51), hover: hsl(224, 64, 45),
};
const DEFAULT_DARK_BRAND: BrandTokens = {
  fill: hsl(224, 76, 60), on: WHITE, soft: hsl(224, 35, 19), line: hsl(224, 40, 32), ink: hsl(224, 76, 60), hover: hsl(224, 76, 54),
};

/**
 * Ανοιχτό: το χρώμα αυτούσιο για τα γεμάτα κουμπιά, με κείμενο textOn. Ως ΚΕΙΜΕΝΟ (--brand-ink, και
 * --ring) σκουραίνει ώσπου να φτάσει 4.5:1 πάνω στη σελίδα και στο απαλό φόντο επιλογής.
 * soft/line: η απόχρωση στο 96% / 86% (όπως το μπλε), λίγο πιο σκούρα για άχρωμα brand.
 */
function lightBrandTokens(b: Hsl, page: Hsl): BrandTokens {
  const k = chroma(b);
  const soft = hsl(b.h, Math.min(90, b.s * 1.33), 96 - 4 * (1 - k));
  const line = hsl(b.h, Math.min(85, b.s * 1.1), 86 - 4 * (1 - k));
  return {
    fill: b,
    on: textOn(b),
    soft,
    line,
    ink: inkFor(b, [page, soft], -1),
    hover: hsl(b.h, b.s, Math.max(0, b.l - 6)),
  };
}

/**
 * Σκούρο: +9% φωτεινότητα (όπως το μπλε 51% → 60%), με δύο όρια:
 *  - το κείμενο του κουμπιού μένει το ίδιο με το ανοιχτό και ≥ 4.5:1 (ή όσο ήταν, αν ήταν ήδη λιγότερο)·
 *  - το κουμπί ξεχωρίζει από τη σελίδα ≥ 3:1 (μαύρο/σκούρο brand ανοίγει, αλλιώς χάνεται στον Γραφίτη).
 * Ως κείμενο (--brand-ink) ανοίγει ώσπου να φτάσει 4.5:1 πάνω στις κάρτες.
 */
function darkBrandTokens(b: Hsl, page: Hsl, card: Hsl): BrandTokens {
  const k = chroma(b);
  const lightOn = textOn(b);
  const onIsWhite = lightOn.l === 100;
  const target = Math.min(b.l + 9, 88);
  let l = b.l;
  if (onIsWhite) {
    const floor = Math.min(AA, contrastRatio(WHITE, b));
    for (let x = b.l; x <= target + 1e-9; x += 0.25) {
      if (contrastRatio(WHITE, hsl(b.h, b.s, x)) >= floor) l = x; else break;
    }
  } else {
    l = target;
  }
  while (contrastRatio(hsl(b.h, b.s, l), page) < 3 && l < 95) l += 0.25;
  const fill = hsl(b.h, b.s, l);
  const on = contrastRatio(onIsWhite ? WHITE : nearBlack(fill), fill) >= AA ? (onIsWhite ? WHITE : nearBlack(fill)) : textOn(fill);
  const soft = hsl(b.h, Math.min(40, b.s * 0.46), 19 + 5 * (1 - k));
  const line = hsl(b.h, Math.min(45, b.s * 0.53), 32 + 4 * (1 - k));
  return {
    fill,
    on,
    soft,
    line,
    ink: inkFor(fill, [card, soft], 1),
    hover: hsl(fill.h, fill.s, Math.max(0, fill.l - 6)),
  };
}

/* ───────────────────────── tokens → CSS ───────────────────────── */

export interface ThemeTokens {
  /** :root (και portals) */
  light: Record<string, string>;
  dark: Record<string, string>;
  /** .cashier-workspace (CashierV2.css ξαναορίζει εκεί τα tokens) */
  lightCashier: Record<string, string>;
  darkCashier: Record<string, string>;
  /** .role-workspace:not(.role-kiosk) (RoleV2.css: PDA, διαχείριση, κουζίνα, διανομέας) */
  lightRole: Record<string, string>;
  darkRole: Record<string, string>;
}

function brandVars(t: BrandTokens): Record<string, string> {
  return {
    '--brand': triplet(t.fill),
    '--brand-soft': triplet(t.soft),
    '--brand-line': triplet(t.line),
    '--on-brand': triplet(t.on),
    '--brand-ink': triplet(t.ink),
    '--primary': triplet(t.fill),
    '--primary-foreground': triplet(t.on),
    '--ring': triplet(t.ink),
    '--workspace-accent': full(t.fill),
    '--workspace-accent-hover': full(t.hover),
    '--workspace-accent-soft': full(t.soft),
    '--workspace-accent-line': full(t.line),
    '--workspace-accent-ink': full(t.ink),
    '--workspace-on-accent': full(t.on),
    '--cashier-accent': full(t.fill),
    '--cashier-accent-hover': full(t.hover),
    '--cashier-accent-soft': full(t.soft),
    '--cashier-accent-line': full(t.line),
    '--cashier-accent-ink': full(t.ink),
    '--cashier-on-accent': full(t.on),
  };
}

function surfaceVars(p: Surfaces): Record<string, string> {
  return {
    '--surface-0': triplet(p.s0),
    '--surface-1': triplet(p.s1),
    '--surface-2': triplet(p.s2),
    '--surface-3': triplet(p.s3),
    '--surface-4': triplet(p.s4),
    '--line': triplet(p.line),
    '--line-strong': triplet(p.lineStrong),
    '--text-1': triplet(p.t1),
    '--text-2': triplet(p.t2),
    '--text-3': triplet(p.t3),
    '--text-4': triplet(p.t4),
  };
}

/** Τα shadcn/Tailwind tokens που το index.css γράφει με τιμές (όχι var()) πάνω στο :root. */
function rootSurfaceVars(p: Surfaces, dark: boolean): Record<string, string> {
  return {
    ...surfaceVars(p),
    '--app-shell': triplet(p.s1),
    '--background': triplet(p.s1),
    '--foreground': triplet(p.t1),
    '--card': triplet(p.s2),
    '--card-foreground': triplet(p.t1),
    '--popover': triplet(p.s2),
    '--popover-foreground': triplet(p.t1),
    '--secondary': triplet(p.s3),
    '--secondary-foreground': triplet(p.t1),
    '--accent': triplet(p.s3),
    '--accent-foreground': triplet(p.t1),
    '--muted': triplet(dark ? p.s3 : p.s1),
    '--muted-foreground': triplet(p.t3),
    '--border': triplet(p.line),
    '--input': triplet(p.line),
  };
}

/**
 * Θέμα → tokens ανά επιλογέα. `full`: ΟΛΑ τα tokens, και όσα είναι στην προεπιλογή (για την
 * προεπισκόπηση, που πρέπει να δείχνει «Χαρτί»/μπλε ακόμη κι όταν η σελίδα έχει άλλο θέμα).
 */
export function deriveThemeTokens(theme: unknown, opts: { full?: boolean } = {}): ThemeTokens {
  const t = toResolved(theme) || { brand: null, surface: null, appearance: null };
  const fullMode = opts.full === true;
  const customBrand = !isDefaultBrand(t.brand);
  const brand = hexToHsl(customBrand ? (t.brand as string) : DEFAULT_BRAND);
  // Χωρίς επιλογή: «Στο χρώμα» (DEFAULT_SURFACE) — όχι το «Χαρτί» του στατικού CSS.
  const surface: ThemeSurface = t.surface || DEFAULT_SURFACE;

  const lightPal = lightSurfaces(surface, brand);
  const darkPal = surface === 'brand' ? brandDark(brand) : GRAPHITE;
  const lightSurfacesCustom = surface !== 'paper';
  const darkSurfacesCustom = surface === 'brand';

  const lightBrand = customBrand ? lightBrandTokens(brand, lightPal.s1) : DEFAULT_LIGHT_BRAND;
  const darkBrand = customBrand ? darkBrandTokens(brand, darkPal.s1, darkPal.s2) : DEFAULT_DARK_BRAND;
  const withBrand = customBrand || fullMode;

  const out: ThemeTokens = { light: {}, dark: {}, lightCashier: {}, darkCashier: {}, lightRole: {}, darkRole: {} };
  if (withBrand) {
    const lb = brandVars(lightBrand);
    const db = brandVars(darkBrand);
    Object.assign(out.light, lb); Object.assign(out.lightCashier, lb); Object.assign(out.lightRole, lb);
    Object.assign(out.dark, db); Object.assign(out.darkCashier, db); Object.assign(out.darkRole, db);
  }
  if (lightSurfacesCustom || fullMode) {
    Object.assign(out.light, rootSurfaceVars(lightPal, false));
    Object.assign(out.lightCashier, surfaceVars(lightPal));
    Object.assign(out.lightRole, surfaceVars(roleVariant(lightPal)));
  }
  if (darkSurfacesCustom || fullMode) {
    Object.assign(out.dark, rootSurfaceVars(darkPal, true));
    Object.assign(out.darkCashier, surfaceVars(darkPal));
    Object.assign(out.darkRole, surfaceVars(darkPal));
  }
  return out;
}

const block = (selector: string, vars: Record<string, string>): string => {
  const entries = Object.entries(vars);
  if (entries.length === 0) return '';
  return `${selector} {\n${entries.map(([k, v]) => `  ${k}: ${v};`).join('\n')}\n}\n`;
};

const LIGHT_ROOT = ':root.look-v2:not(.dark)';
const DARK_ROOT = ':root.look-v2.dark';
const ROLE = '.role-workspace:not(.role-kiosk)';

/**
 * Το stylesheet του θέματος (καθαρή συνάρτηση). Χωρίς `scope`: κανόνες για :root.look-v2(:not).dark,
 * .cashier-workspace και .role-workspace — ίδια ειδικότητα με τα αρχεία της εφαρμογής, γι' αυτό το
 * <style> μπαίνει τελευταίο. Θέμα στην προεπιλογή → κενό κείμενο.
 * Με `scope` (προεπισκόπηση): `${scope}[data-mode="light"]` / `${scope}[data-mode="dark"]` με όλα τα
 * tokens, ώστε ένα κουτί της σελίδας να δείχνει το θέμα όποιο κι αν ισχύει γύρω του.
 */
export function deriveThemeCss(theme: unknown, opts: { scope?: string; full?: boolean } = {}): string {
  const scoped = typeof opts.scope === 'string' && opts.scope.length > 0;
  const tk = deriveThemeTokens(theme, { full: opts.full ?? scoped });
  if (scoped) {
    return block(`${opts.scope}[data-mode="light"]`, { ...tk.light, ...tk.lightCashier })
      + block(`${opts.scope}[data-mode="dark"]`, { ...tk.dark, ...tk.darkCashier });
  }
  const css = block(LIGHT_ROOT, tk.light)
    + block(`${LIGHT_ROOT} .cashier-workspace`, tk.lightCashier)
    + block(`${LIGHT_ROOT} ${ROLE}`, tk.lightRole)
    + block(DARK_ROOT, tk.dark)
    + block(`${DARK_ROOT} .cashier-workspace`, tk.darkCashier)
    + block(`${DARK_ROOT} ${ROLE}`, tk.darkRole);
  return css ? `/* Εμφάνιση καταστήματος — utils/storeTheme.ts */\n${css}` : '';
}

/* ───────────────────────── εφαρμογή στη σελίδα ───────────────────────── */

export const STORE_THEME_STYLE_ID = 'store-theme';
/** Τελευταίο θέμα της συσκευής: εφαρμόζεται πριν από το /me, χωρίς αναβόσβημα του μπλε σε κάθε F5. */
export const STORE_THEME_CACHE_KEY = 'xentrix_store_theme';
/** window CustomEvent με detail = ResolvedStoreTheme | null, σε κάθε αλλαγή. */
export const STORE_THEME_EVENT = 'xentrix:store-theme';

let current: ResolvedStoreTheme | null = null;
const listeners = new Set<() => void>();

const readCache = (): ResolvedStoreTheme | null => {
  try {
    const raw = localStorage.getItem(STORE_THEME_CACHE_KEY);
    return raw ? toResolved(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

/**
 * Γράφει (ή σβήνει) το <style id="store-theme"> και το κρατά ΤΕΛΕΥΤΑΙΟ στο <head>. Χωρίς θέμα (αποσύνδεση,
 * κατάστημα χωρίς ρύθμιση) γράφεται η ΠΡΟΕΠΙΛΟΓΗ εφαρμογής (DEFAULT_SURFACE «Στο χρώμα» — το στατικό CSS
 * είναι «Χαρτί»)· μόνο το «Χαρτί» με το μπλε δεν γράφει τίποτα. `persist:false` = χωρίς εγγραφή στο localStorage.
 */
export function applyStoreTheme(theme: unknown, opts: { persist?: boolean } = {}): void {
  const next = toResolved(theme);
  const changed = JSON.stringify(next) !== JSON.stringify(current);
  current = next;
  if (typeof document !== 'undefined') {
    const css = deriveThemeCss(next);
    let el = document.getElementById(STORE_THEME_STYLE_ID) as HTMLStyleElement | null;
    if (!css) {
      el?.remove();
    } else {
      if (!el) {
        el = document.createElement('style');
        el.id = STORE_THEME_STYLE_ID;
      }
      if (el.textContent !== css) el.textContent = css;
      if (document.head.lastElementChild !== el) document.head.appendChild(el);
    }
  }
  if (opts.persist !== false) {
    try {
      if (next) localStorage.setItem(STORE_THEME_CACHE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORE_THEME_CACHE_KEY);
    } catch { /* private mode — απλώς χωρίς cache */ }
  }
  if (changed) {
    listeners.forEach((fn) => { try { fn(); } catch { /* ένας listener δεν ρίχνει τους άλλους */ } });
    try { window.dispatchEvent(new CustomEvent(STORE_THEME_EVENT, { detail: next })); } catch { /* παλιός browser */ }
  }
}

/** Αποσύνδεση: πίσω στην εμφάνιση της εφαρμογής και χωρίς cache. */
export function clearStoreTheme(): void {
  applyStoreTheme(null);
}

/** Στην εκκίνηση (πριν από το πρώτο render): το θέμα της τελευταίας σύνδεσης αυτής της συσκευής. */
export function restoreCachedStoreTheme(): void {
  const cached = readCache();
  if (cached) applyStoreTheme(cached, { persist: false });
}

/** Το θέμα που ισχύει τώρα στη σελίδα (ή null). */
export function getAppliedStoreTheme(): ResolvedStoreTheme | null {
  return current;
}

/**
 * Η προεπιλογή ανοιχτό/σκούρο/σύστημα του καταστήματος, για συσκευή ΧΩΡΙΣ δική της επιλογή
 * (xentrix_theme). null = καμία προεπιλογή (ό,τι κάνει η εφαρμογή). Διαβάζεται και πριν από το /me,
 * από το cache της τελευταίας σύνδεσης. Για αλλαγές: subscribeStoreTheme / useStoreDefaultAppearance.
 */
export function getStoreDefaultAppearance(): ThemeAppearance | null {
  return (current ?? readCache())?.appearance ?? null;
}

/** Ειδοποίηση σε κάθε αλλαγή θέματος (εφαρμογή/αφαίρεση). Επιστρέφει το unsubscribe. */
export function subscribeStoreTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** React: η προεπιλογή εμφάνισης του καταστήματος, με νέα απόδοση όταν αλλάζει. */
export function useStoreDefaultAppearance(): ThemeAppearance | null {
  return useSyncExternalStore(subscribeStoreTheme, getStoreDefaultAppearance, () => null);
}
